import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../../../components/contexts/SocketContext';
import { useAuth } from '../../../components/contexts/AuthContext';
import { getPhotoTags, putPhotoTags } from '../models/photoTags';

export function usePhotoTags(queryPk, open = true) {
  const { socket } = useSocket(), { authState } = useAuth();
  const [data, setData] = useState(null), [edits, setEdits] = useState({}), [notice, setNotice] = useState(''), [busy, setBusy] = useState(false);
  const scope = `${authState?.id}:${queryPk}:${open}`, active = useRef(scope), dirty = useRef(edits), pending = useRef(false), sequence = useRef(0);
  active.current = scope; dirty.current = edits;
  const load = useCallback(async () => {
    const ticket = ++sequence.current;
    if (!open || !queryPk) return;
    try { const result = await getPhotoTags(queryPk); if (active.current === scope && ticket === sequence.current) setData(result); }
    catch (error) { if (active.current === scope && ticket === sequence.current) setNotice(error?.response?.data?.message || 'Photo tags not available.'); }
  }, [queryPk, open, scope]);
  useEffect(() => { const counter = sequence; active.current = scope; setData(null); setEdits({}); setNotice(''); load(); return () => { counter.current++; active.current = null; }; }, [load, scope]);
  useEffect(() => {
    if (!open || !socket) return undefined;
    const changed = event => { if (String(event?.query_pk) !== String(queryPk)) return; if (Object.keys(dirty.current).length) setNotice('Tags changed elsewhere'); else load(); };
    socket.on('query:photo_tags_updated', changed);
    socket.on('query:parts_updated', changed);
    return () => { socket.off('query:photo_tags_updated', changed); socket.off('query:parts_updated', changed); };
  }, [socket, open, queryPk, load]);
  async function save(tags = edits) {
    if (pending.current || !data || !Object.keys(tags).length) return !Object.keys(tags).length;
    pending.current = true; setBusy(true);
    try { const result = await putPhotoTags(queryPk, data.version, tags); if (active.current === scope) { setData(result); setEdits({}); setNotice(''); } return true; }
    catch (error) {
      if (active.current === scope) {
        if (error?.response?.data?.code === 'PHOTO_TAGS_CHANGED') { setData(error.response.data.data); setEdits({}); setNotice('Photo tags were changed by someone else. Reloaded.'); }
        else setNotice("Photos saved, but their part tags weren't. Retry?");
      }
      return false;
    } finally { pending.current = false; if (active.current === scope) setBusy(false); }
  }
  const change = (id, ids) => setEdits(previous => ({ ...previous, [id]: ids }));
  const photo = id => Object.prototype.hasOwnProperty.call(edits, id) ? { image_id: id, general: !edits[id].length, query_part_ids: edits[id], part_removed: false } : data?.photos.find(row => row.image_id === id);
  return { data, edits, notice, busy, load, save, change, photo, replaceEdits: setEdits };
}
